import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { Box } from '@saviynt/design-system';
import cx from 'classnames';

import type { RootState } from '../../../shared/types';
import { AuditLogViewerHeader } from '../AuditLogViewerHeader';
import { auditLogActions } from '../data-store';

import { AuditLogViewerAuditorLeftBody } from './AuditLogViewerAuditorLeftBody';
import { AuditLogViewerAuditorSmartFilters } from './AuditLogViewerAuditorSmartFilters';
import { type AuditLogViewerAuditorProps } from './types';

import './AuditLogViewerAuditor.css';

type TabType = 'transaction-overview' | 'all-logs';

/**
 * AuditLogViewerAuditor Component
 *
 * Main component for the auditor persona view of the audit log viewer.
 */
function AuditLogViewerAuditor({
  className,
}: AuditLogViewerAuditorProps): React.ReactElement {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const [activeTab, setActiveTab] = useState<TabType>('transaction-overview');
  const [isSmartFiltersOpen, setIsSmartFiltersOpen] = useState(true);
  const [panelStates, setPanelStates] = useState({
    isInsightsOpen: false, // Insights panel not shown in readOnly mode (auditor view)
    isRecordingOpen: false,
    isDetailPanelOpen: false,
    isTransactionSidePanelOpen: false,
  });
  // Get request metadata first

  const handleTabChange = (newTab: TabType) => {
    setActiveTab(newTab);
  };

  const handlePanelStatesChange = (newStates: Partial<typeof panelStates>) => {
    setPanelStates((prev) => ({ ...prev, ...newStates }));
  };

  const handleSmartFiltersClose = () => {
    setIsSmartFiltersOpen(false);
  };

  const handleSmartFiltersToggle = () => {
    // Close recording panel if smart filters are being opened
    if (!isSmartFiltersOpen && panelStates.isRecordingOpen) {
      setPanelStates((prev) => ({
        ...prev,
        isRecordingOpen: false,
      }));
    }

    // Close other side panels that conflict with smart filters
    const hasConflictingPanels =
      panelStates.isTransactionSidePanelOpen ||
      panelStates.isDetailPanelOpen ||
      panelStates.isInsightsOpen;

    if (hasConflictingPanels) {
      setPanelStates((prev) => ({
        ...prev,
        isTransactionSidePanelOpen: false,
        isDetailPanelOpen: false,
        isInsightsOpen: false,
      }));
    }

    // Then toggle smart filters
    setIsSmartFiltersOpen(!isSmartFiltersOpen);
  };

  // Persist initial active tab context to Redux for filters switching
  useEffect(() => {
    dispatch(auditLogActions.setActiveTab('transaction-overview'));
  }, [dispatch]);

  return (
    <Box
      className={cx('AuditLogViewerAuditor', className, {
        'Sod-AuditLogViewer--withInsightsPanel':
          activeTab === 'all-logs' && panelStates.isInsightsOpen,
        'Sod-AuditLogViewer--withRecordingPanel':
          activeTab === 'all-logs' && panelStates.isRecordingOpen,
        'Sod-AuditLogViewer--withDetailPanel':
          activeTab === 'all-logs' &&
          panelStates.isDetailPanelOpen &&
          !panelStates.isRecordingOpen,
        'Sod-AuditLogViewer--withSmartFilters':
          activeTab === 'all-logs' && isSmartFiltersOpen,
      })}>
      {/* Header with auditor-specific functionality */}
      <AuditLogViewerHeader onAuditorDetailsClick={handleSmartFiltersToggle} />
      {/* Smart filters expanded - show when panel is open */}
      {isSmartFiltersOpen && (
        <AuditLogViewerAuditorSmartFilters
          onClose={handleSmartFiltersClose}
          onNavigateToAllLogs={() => setActiveTab('all-logs')}
        />
      )}
      {/* Left Body */}
      <AuditLogViewerAuditorLeftBody
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onPanelStatesChange={handlePanelStatesChange}
        isSmartFiltersOpen={isSmartFiltersOpen}
        onSmartFiltersClose={handleSmartFiltersClose}
        panelStates={panelStates}
      />
    </Box>
  );
}

export default AuditLogViewerAuditor;
